import { useState, useEffect, useRef, useMemo } from 'react';
import { diffLines } from 'diff';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, Spinner, Modal } from './shared';

const STEP_CONFIGURE = 'configure';
const STEP_PREVIEW = 'preview';
const STEP_RESULT = 'result';

export default function GithubImportModal({ open, mode = 'add', onClose, onSuccess }) {
    const [step, setStep] = useState(STEP_CONFIGURE);
    const [config, setConfig] = useState({ url: '', paths: [], branch: '', has_token: false });
    const [tokenInput, setTokenInput] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [editingToken, setEditingToken] = useState(false);
    const [loading, setLoading] = useState(false);
    const [configLoading, setConfigLoading] = useState(false);
    const [error, setError] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [syncResult, setSyncResult] = useState(null);

    useEffect(() => {
        if (open) {
            setStep(STEP_CONFIGURE);
            setError(null);
            setPreviewData(null);
            setSyncResult(null);
            setShowToken(false);
            setEditingToken(false);
            setTokenInput('');
            loadConfig();
        }
    }, [open]);

    const loadConfig = async () => {
        setConfigLoading(true);
        try {
            const result = await adminApi.getGithubConfig();
            setConfig({
                url: result.url || '',
                paths: result.paths || [],
                branch: result.branch || '',
                has_token: result.has_token || false,
            });
            setEditingToken(!result.has_token);
        } catch (err) {
            console.error('[GitHub] Failed to load config:', err);
        } finally {
            setConfigLoading(false);
        }
    };

    const extractError = (err) => {
        if (err?.response) {
            try {
                const body = typeof err.response === 'string' ? JSON.parse(err.response) : err.response;
                if (body?.detail) return body.detail;
            } catch {}
        }
        return err.message || 'Unknown error';
    };

    const handleAddConnection = async () => {
        if (!config.url.trim()) {
            setError('GitHub URL is required');
            return;
        }
        const token = tokenInput.trim() || null;
        if (!config.has_token && !token) {
            setError('Personal access token is required');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await adminApi.testGithubConnection({
                url: config.url.trim(),
                paths: config.paths,
                branch: config.branch.trim(),
                token,
            });
            setConfig(prev => ({ ...prev, has_token: true }));
            setEditingToken(false);
            setTokenInput('');
            onSuccess?.(`Connected to ${result.repo}. ${result.message}`);
            onClose();
        } catch (err) {
            setError(extractError(err));
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = tokenInput.trim() || null;
            await adminApi.saveGithubConfig({
                url: config.url.trim(),
                paths: config.paths,
                branch: config.branch.trim(),
                token,
            });
            const result = await adminApi.previewGithubSkills({ token });
            setPreviewData(result);
            const allIds = new Set(
                (result.skills || [])
                    .filter(s => !s.validation_error)
                    .map(s => s.skill_id)
            );
            setSelectedIds(allIds);
            setStep(STEP_PREVIEW);
        } catch (err) {
            setError(extractError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        if (selectedIds.size === 0) {
            setError('Select at least one skill to sync');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const existingSelected = (previewData?.skills || []).filter(
                s => selectedIds.has(s.skill_id) && s.exists
            );
            const result = await adminApi.syncGithubSkills({
                token: tokenInput.trim() || null,
                skillIds: Array.from(selectedIds),
                overwrite: existingSelected.length > 0,
            });
            setSyncResult(result);
            setStep(STEP_RESULT);
            const syncedCount = result.synced?.length || 0;
            if (syncedCount > 0) {
                onSuccess?.(`Synced ${syncedCount} skill${syncedCount !== 1 ? 's' : ''} from GitHub`);
            }
        } catch (err) {
            setError(extractError(err));
        } finally {
            setLoading(false);
        }
    };

    const toggleSkill = (skillId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(skillId)) next.delete(skillId); else next.add(skillId);
            return next;
        });
    };

    const toggleAll = () => {
        const validSkills = (previewData?.skills || []).filter(s => !s.validation_error);
        if (selectedIds.size === validSkills.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(validSkills.map(s => s.skill_id)));
        }
    };

    const titleByStep = {
        [STEP_CONFIGURE]: mode === 'add' ? 'Add GitHub Source' : 'Sync from GitHub',
        [STEP_PREVIEW]: `Preview — ${previewData?.repo || ''}`,
        [STEP_RESULT]: 'Sync Complete',
    };

    return (
        <Modal open={open} onClose={() => !loading && onClose()} title={titleByStep[step]} size="lg">
            <Modal.Body>
                {configLoading ? (
                    <Spinner size="md" text="Loading config..." center />
                ) : (
                    <>
                        {step === STEP_CONFIGURE && (
                            <ConfigureStep
                                config={config}
                                tokenInput={tokenInput}
                                showToken={showToken}
                                editingToken={editingToken}
                                mode={mode}
                                error={error}
                                loading={loading}
                                onConfigChange={(field, value) => setConfig(prev => ({ ...prev, [field]: value }))}
                                onTokenChange={setTokenInput}
                                onToggleToken={() => setShowToken(v => !v)}
                                onEditToken={() => setEditingToken(true)}
                                onCancelEditToken={() => { setEditingToken(false); setTokenInput(''); }}
                            />
                        )}
                        {step === STEP_PREVIEW && (
                            <PreviewStep
                                previewData={previewData}
                                selectedIds={selectedIds}
                                error={error}
                                onToggleSkill={toggleSkill}
                                onToggleAll={toggleAll}
                            />
                        )}
                        {step === STEP_RESULT && (
                            <ResultStep syncResult={syncResult} />
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {step === STEP_CONFIGURE && mode === 'add' && (
                    <>
                        <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
                        <Button onClick={handleAddConnection} loading={loading}>Test & Save</Button>
                    </>
                )}
                {step === STEP_CONFIGURE && mode === 'sync' && (
                    <>
                        <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
                        <Button onClick={handlePreview} loading={loading}>Preview Skills</Button>
                    </>
                )}
                {step === STEP_PREVIEW && (
                    <>
                        <Button variant="ghost" onClick={() => { setStep(STEP_CONFIGURE); setError(null); }} disabled={loading}>Back</Button>
                        <Button onClick={handleSync} loading={loading} disabled={selectedIds.size === 0}>
                            Sync {selectedIds.size > 0 ? `${selectedIds.size} Skill${selectedIds.size !== 1 ? 's' : ''}` : ''}
                        </Button>
                    </>
                )}
                {step === STEP_RESULT && (
                    <Button onClick={onClose}>Done</Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

function PathsInput({ paths, onChange, disabled }) {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);

    const addPath = () => {
        const trimmed = inputValue.trim().replace(/^\/+|\/+$/g, '');
        if (!trimmed || paths.includes(trimmed)) {
            setInputValue('');
            return;
        }
        onChange([...paths, trimmed]);
        setInputValue('');
    };

    const removePath = (index) => {
        onChange(paths.filter((_, i) => i !== index));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addPath();
        } else if (e.key === 'Backspace' && !inputValue && paths.length > 0) {
            onChange(paths.slice(0, -1));
        }
    };

    return (
        <div>
            <div
                className="min-h-[38px] w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 flex flex-wrap gap-1.5 items-center cursor-text focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent"
                onClick={() => inputRef.current?.focus()}
            >
                {paths.map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 text-xs font-mono shrink-0">
                        {p}
                        {!disabled && (
                            <button
                                type="button"
                                className="text-blue-400 hover:text-blue-700 leading-none"
                                onClick={(e) => { e.stopPropagation(); removePath(i); }}
                            >
                                ×
                            </button>
                        )}
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    className="flex-1 min-w-[120px] outline-none bg-transparent text-sm font-mono placeholder-gray-400"
                    placeholder={paths.length === 0 ? 'skills/tier1-orchestrators' : 'Add path...'}
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={addPath}
                    disabled={disabled}
                />
            </div>
            <p className="text-xs text-gray-400 mt-1">Type a path and press Enter to add. Each path scanned 1 level deep for SKILL.md folders.</p>
        </div>
    );
}

function ConfigureStep({ config, tokenInput, showToken, editingToken, mode, error, loading, onConfigChange, onTokenChange, onToggleToken, onEditToken, onCancelEditToken }) {
    return (
        <div className="space-y-4">
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">GitHub Repository URL</label>
                <input
                    type="url"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://github.com/org/repo"
                    value={config.url}
                    onChange={e => onConfigChange('url', e.target.value)}
                    disabled={loading}
                />
            </div>

            <div>
                <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-gray-700">Personal Access Token</label>
                    {config.has_token && !editingToken && (
                        <button type="button" className="text-xs text-blue-500 hover:underline" onClick={onEditToken}>
                            Update token
                        </button>
                    )}
                </div>
                {config.has_token && !editingToken ? (
                    <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
                        <span className="text-xs text-emerald-600 font-medium">Token saved</span>
                        <span className="text-xs text-gray-400">— stored securely on server</span>
                    </div>
                ) : (
                    <>
                        <div className="relative">
                            <input
                                type={showToken ? 'text' : 'password'}
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                                placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                                value={tokenInput}
                                onChange={e => onTokenChange(e.target.value)}
                                disabled={loading}
                                autoComplete="new-password"
                                autoFocus={editingToken && config.has_token}
                            />
                            <button
                                type="button"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                onClick={onToggleToken}
                                tabIndex={-1}
                            >
                                {showToken ? (
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                    </svg>
                                ) : (
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {config.has_token && editingToken && (
                            <button type="button" className="text-xs text-gray-400 hover:text-gray-600 mt-1" onClick={onCancelEditToken}>
                                Cancel — keep existing token
                            </button>
                        )}
                    </>
                )}
                {!config.has_token && (
                    <p className="text-xs text-gray-400 mt-1">Requires <code className="bg-gray-100 px-1 rounded">repo</code> scope for private repositories</p>
                )}
            </div>

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    Branch <span className="text-gray-400 font-normal">(optional — leave empty for default branch)</span>
                </label>
                <input
                    type="text"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                    placeholder="main"
                    value={config.branch}
                    onChange={e => onConfigChange('branch', e.target.value)}
                    disabled={loading}
                />
            </div>

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    Scan Paths <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <PathsInput
                    paths={config.paths}
                    onChange={paths => onConfigChange('paths', paths)}
                    disabled={loading}
                />
            </div>

            {mode === 'sync' && config.has_token && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                    Config changes are auto-saved when you click "Preview Skills".
                </div>
            )}

            {error && <Alert variant="error">{error}</Alert>}
        </div>
    );
}

function PreviewStep({ previewData, selectedIds, error, onToggleSkill, onToggleAll }) {
    const skills = previewData?.skills || [];
    const validSkills = skills.filter(s => !s.validation_error);
    const allSelected = validSkills.length > 0 && selectedIds.size === validSkills.length;
    const existingSelected = validSkills.filter(s => s.exists && selectedIds.has(s.skill_id));
    const paths = previewData?.paths || [];
    const branch = previewData?.branch;

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 space-y-0.5">
                    <div>
                        Found <span className="font-medium text-gray-900">{skills.length}</span> skill folder{skills.length !== 1 ? 's' : ''} in <code className="text-xs bg-gray-100 px-1 rounded">{previewData?.repo}</code>
                        {branch && <> @ <code className="text-xs bg-gray-100 px-1 rounded">{branch}</code></>}
                    </div>
                    {paths.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-xs text-gray-400">Paths:</span>
                            {paths.map(p => <code key={p} className="text-xs bg-gray-100 px-1 rounded">{p}</code>)}
                        </div>
                    )}
                </div>
                {validSkills.length > 0 && (
                    <button className="text-xs text-blue-600 hover:underline shrink-0 ml-2" onClick={onToggleAll}>
                        {allSelected ? 'Deselect all' : 'Select all'}
                    </button>
                )}
            </div>

            {existingSelected.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <span className="text-xs text-amber-800">
                        {existingSelected.length} selected skill{existingSelected.length !== 1 ? 's' : ''} will be overridden: {existingSelected.map(s => s.skill_id).join(', ')}
                    </span>
                </div>
            )}

            {previewData?.errors?.length > 0 && (
                <Alert variant="error">
                    {previewData.errors.length} folder error{previewData.errors.length !== 1 ? 's' : ''}: {previewData.errors.join('; ')}
                </Alert>
            )}

            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-80 overflow-y-auto">
                {skills.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                        No skill folders found. Check that your paths contain subdirectories with <code className="bg-gray-100 px-1 rounded">SKILL.md</code>.
                    </div>
                ) : (
                    skills.map((skill) => (
                        <SkillPreviewRow
                            key={skill.skill_id}
                            skill={skill}
                            checked={selectedIds.has(skill.skill_id)}
                            onToggle={() => !skill.validation_error && onToggleSkill(skill.skill_id)}
                        />
                    ))
                )}
            </div>

            {error && <Alert variant="error">{error}</Alert>}
        </div>
    );
}

function DiffModal({ open, onClose, skillId, oldContent, newContent }) {
    const changes = useMemo(() => {
        if (!oldContent || !newContent || oldContent === newContent) return null;
        return diffLines(oldContent, newContent);
    }, [oldContent, newContent]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[calc(100vh-4rem)]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">SKILL.md diff — {skillId}</h3>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-100 border border-red-300 inline-block" />removed</span>
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-100 border border-green-300 inline-block" />added</span>
                        </div>
                    </div>
                    <button
                        className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        onClick={onClose}
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="overflow-auto flex-1">
                    {!changes ? (
                        <p className="text-sm text-gray-400 text-center py-8">SKILL.md content is identical — no changes.</p>
                    ) : (
                        <pre className="text-xs leading-5 font-mono">
                            {changes.map((part, i) => {
                                const lines = part.value.split('\n').filter((_, idx, arr) => idx < arr.length - 1 || part.value.endsWith('\n') || arr[idx] !== '');
                                const prefix = part.added ? '+' : part.removed ? '-' : ' ';
                                const cls = part.added
                                    ? 'text-green-800 bg-green-50'
                                    : part.removed
                                        ? 'text-red-800 bg-red-50'
                                        : 'text-gray-500';
                                return lines.map((line, j) => (
                                    <div key={`${i}-${j}`} className={`px-4 whitespace-pre ${cls}`}>
                                        {prefix}{line}
                                    </div>
                                ));
                            })}
                        </pre>
                    )}
                </div>
                <div className="px-4 py-3 border-t border-gray-200 flex justify-end shrink-0">
                    <button
                        className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

function SkillPreviewRow({ skill, checked, onToggle }) {
    const [expanded, setExpanded] = useState(false);
    const [diffOpen, setDiffOpen] = useState(false);
    const hasConflict = skill.exists && skill.existing_skill;
    const hasDiff = hasConflict && skill.existing_content && skill.incoming_content && skill.existing_content !== skill.incoming_content;

    return (
        <>
            <DiffModal
                open={diffOpen}
                onClose={() => setDiffOpen(false)}
                skillId={skill.skill_id}
                oldContent={skill.existing_content}
                newContent={skill.incoming_content}
            />
            <div className={`px-3 py-2.5 ${skill.validation_error ? 'bg-red-50/50' : ''}`}>
                <div className="flex items-start gap-2.5">
                    <input
                        type="checkbox"
                        className="mt-0.5 rounded border-gray-300 text-blue-600 disabled:opacity-40"
                        checked={checked}
                        onChange={onToggle}
                        disabled={!!skill.validation_error}
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-sm font-medium text-gray-900 truncate">{skill.skill_name || skill.skill_id}</span>
                            <Badge variant="outline" className="text-xs shrink-0">{skill.skill_id}</Badge>
                            {skill.exists && !hasDiff && <Badge variant="warning" className="text-xs shrink-0">Exists (no change)</Badge>}
                            {skill.exists && hasDiff && <Badge variant="warning" className="text-xs shrink-0">Exists (modified)</Badge>}
                            {skill.validation_error && <Badge variant="danger" className="text-xs shrink-0">Invalid</Badge>}
                        </div>
                        {skill.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{skill.description}</p>}
                        {skill.validation_error && <p className="text-xs text-red-500 mt-0.5">{skill.validation_error}</p>}
                        <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-400">{skill.files.length} file{skill.files.length !== 1 ? 's' : ''}</span>
                            {hasConflict && (
                                <>
                                    <button className="text-xs text-amber-600 hover:underline" onClick={() => setExpanded(v => !v)}>
                                        {expanded ? 'Hide' : 'Compare versions'}
                                    </button>
                                    {hasDiff && (
                                        <button className="text-xs text-blue-600 hover:underline" onClick={() => setDiffOpen(true)}>
                                            Xem thay đổi
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                        {expanded && hasConflict && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <div className="rounded border border-gray-200 bg-white p-2">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Current</div>
                                    <div className="text-xs text-gray-700">{skill.existing_skill.name}</div>
                                    {skill.existing_skill.description && <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{skill.existing_skill.description}</div>}
                                    <div className="text-xs text-gray-400 mt-1">{skill.existing_skill.resource_count} resources</div>
                                </div>
                                <div className="rounded border border-blue-200 bg-blue-50 p-2">
                                    <div className="text-xs font-medium text-blue-600 mb-1">Incoming</div>
                                    <div className="text-xs text-gray-700">{skill.skill_name}</div>
                                    {skill.description && <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{skill.description}</div>}
                                    <div className="text-xs text-gray-400 mt-1">{skill.files.length} files</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

function ResultStep({ syncResult }) {
    if (!syncResult) return null;
    const { synced = [], skipped = [], errors = [] } = syncResult;
    return (
        <div className="space-y-3">
            {synced.length > 0 && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
                    <div className="text-xs font-medium text-green-800 mb-1.5">Synced ({synced.length})</div>
                    <div className="flex flex-wrap gap-1">{synced.map(id => <Badge key={id} variant="success" className="text-xs">{id}</Badge>)}</div>
                </div>
            )}
            {skipped.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                    <div className="text-xs font-medium text-gray-600 mb-1.5">Skipped ({skipped.length})</div>
                    <div className="flex flex-wrap gap-1">{skipped.map(id => <Badge key={id} variant="outline" className="text-xs">{id}</Badge>)}</div>
                </div>
            )}
            {errors.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                    <div className="text-xs font-medium text-red-800 mb-1.5">Errors ({errors.length})</div>
                    <div className="space-y-1">
                        {errors.map((e, i) => <div key={i} className="text-xs text-red-700"><span className="font-medium">{e.skill}</span>: {e.error}</div>)}
                    </div>
                </div>
            )}
            {synced.length === 0 && skipped.length === 0 && errors.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">Nothing to sync.</p>
            )}
        </div>
    );
}
